import { styled } from "goober";
import { useMemo } from "react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { Button } from "../Button";
import { GhostPill } from "../GhostPill";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { PressButton } from "../PressButton";
import { SearchField } from "../Settings/shared";
import { MetaSep } from "../MetaSeparator";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  computeSpaceExecutionCountRows,
  computeWorkflowExecutionCountRows,
  executionCountBarFraction,
  formatExecutionCount,
  workflowRowsForSpace,
} from "./executionCounts";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
} from "./MonitoringDetailCard";
import {
  Bar,
  BarColumn,
  BarRow,
  barColumnStyles,
  ColumnMeta,
  FadeScrollColumns,
} from "./monitoringBars";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { splitBySharePrefix } from "./foldSegments";
import { spaceColorMap } from "./monitoringPanelLayout";
import { OTHER_SPACES_KEY } from "./spaceSegments";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

// Interactive column (opens the per-space modal); the visuals are shared with
// Spend-by-model via barColumnStyles / the other primitives in monitoringBars.
const Column = styled(PressButton)`
  ${barColumnStyles}
`;

// Widest bar row (grid-column: span 2) fits far more bars than the dense donuts,
// so it folds only a long tail instead of reusing the tighter shared SPACE_FOLD.
const EXECUTIONS_FOLD = { minShare: 0, maxKept: 12 };

const GHOST_COLUMNS = [
  { key: "ghost-1", count: 1200, labelWidth: 48, countWidth: 26 },
  { key: "ghost-2", count: 640, labelWidth: 40, countWidth: 22 },
  { key: "ghost-3", count: 210, labelWidth: 56, countWidth: 20 },
  { key: "ghost-4", count: 80, labelWidth: 36, countWidth: 18 },
  { key: "ghost-5", count: 12, labelWidth: 44, countWidth: 16 },
];

type SpaceRow = {
  spaceId: string;
  space: DisplayableSpace | null;
  label: string;
  executionCount: number;
  workflowCount: number;
};

export function ExecutionsBySpace() {
  const recordInteraction = useMonitoringInteraction();
  const { report, loading } = useMonitoringWorkflows();
  const { spaces, loading: spacesLoading } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();
  const {
    panel,
    space,
    search: searchParam,
  } = useCurrentUrl(routes.monitoring.workflows);
  const open = panel === "executions";
  const selectedSpaceId = space ?? null;
  const search = searchParam ?? "";
  const setSearch = (value: string) =>
    navigate(routes.monitoring.workflows, { search: value }, { replace: true });
  const close = () =>
    navigate(
      routes.monitoring.workflows,
      { panel: "", space: "", search: "" },
      { replace: true }
    );

  const { chartRows, totalExecutions, workflowRows, foldedSpaceIds } =
    useMemo(() => {
      const inScope = spaces.filter((space) => matchesSpace(space.id));
      const inScopeIds = inScope.map((space) => space.id);
      const workflowRows = computeWorkflowExecutionCountRows(report.workflows);
      const executionsBySpace = computeSpaceExecutionCountRows(
        workflowRows,
        inScopeIds
      );

      const rows: SpaceRow[] = [];
      let personalExec = 0;
      let personalWf = 0;
      for (const row of executionsBySpace) {
        if (foldsPersonal(scope) && personalIds.has(row.spaceId)) {
          personalExec += row.executionCount;
          personalWf += row.workflowCount;
          continue;
        }
        const space = inScope.find((entry) => entry.id === row.spaceId)!;
        rows.push({
          spaceId: row.spaceId,
          space,
          label: displayName(space),
          executionCount: row.executionCount,
          workflowCount: row.workflowCount,
        });
      }
      if (personalExec > 0) {
        rows.push({
          spaceId: PERSONAL_BUCKET,
          space: null,
          label: "Personal",
          executionCount: personalExec,
          workflowCount: personalWf,
        });
      }
      rows.sort(
        (a, b) =>
          b.executionCount - a.executionCount || a.label.localeCompare(b.label)
      );

      const totalExecutions = rows.reduce(
        (sum, row) => sum + row.executionCount,
        0
      );

      // Fold the small-execution tail into one "Other" bar; its drill-in lists the
      // folded spaces' workflows so it isn't a dead end.
      const { kept, folded } = splitBySharePrefix(
        rows,
        (row) => row.executionCount,
        totalExecutions,
        EXECUTIONS_FOLD
      );
      const foldedSpaceIds = new Set<string>();
      for (const row of folded) {
        if (row.spaceId === PERSONAL_BUCKET)
          for (const id of personalIds) foldedSpaceIds.add(id);
        else foldedSpaceIds.add(row.spaceId);
      }
      const chartRows: SpaceRow[] =
        folded.length > 0
          ? [
              ...kept,
              {
                spaceId: OTHER_SPACES_KEY,
                space: null,
                label: `${folded.length} more spaces`,
                executionCount: folded.reduce(
                  (sum, row) => sum + row.executionCount,
                  0
                ),
                workflowCount: folded.reduce(
                  (sum, row) => sum + row.workflowCount,
                  0
                ),
              },
            ]
          : [...kept];

      return { chartRows, totalExecutions, workflowRows, foldedSpaceIds };
    }, [report, spaces, scope, personalIds, matchesSpace, displayName]);

  const selectedRow = chartRows.find((row) => row.spaceId === selectedSpaceId);
  const selectedWorkflowRows = useMemo(() => {
    if (!selectedRow) return [];
    if (selectedRow.spaceId === PERSONAL_BUCKET) {
      return workflowRows.filter((row) => personalIds.has(row.spaceId));
    }
    if (selectedRow.spaceId === OTHER_SPACES_KEY) {
      return workflowRows.filter((row) => foldedSpaceIds.has(row.spaceId));
    }
    return workflowRowsForSpace(workflowRows, selectedRow.spaceId);
  }, [selectedRow, workflowRows, personalIds, foldedSpaceIds]);
  const filteredWorkflowRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return selectedWorkflowRows;
    return selectedWorkflowRows.filter((row) =>
      row.name.toLowerCase().includes(q)
    );
  }, [selectedWorkflowRows, search]);

  const colorMap = useMemo(() => {
    const map = new Map(spaceColorMap(spaces));
    map.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);
    map.set(OTHER_SPACES_KEY, "var(--gray-600)");
    return map;
  }, [spaces]);
  const maxCount = Math.max(1, ...chartRows.map((row) => row.executionCount));

  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Executions by space</Text>
        <TotalRow>
          <Text variant="display-md">
            {formatExecutionCount(totalExecutions)}
          </Text>
        </TotalRow>
      </PanelHeader>

      <FadeScrollColumns ariaHidden={chartRows.length === 0}>
        {chartRows.length === 0
          ? GHOST_COLUMNS.map((row) => (
              <BarColumn key={row.key}>
                <BarRow>
                  <Bar
                    style={{
                      width: 6,
                      background: "var(--alpha8)",
                      minHeight: 6,
                      height: `${executionCountBarFraction(row.count, GHOST_COLUMNS[0]!.count) * 100}%`,
                    }}
                  />
                </BarRow>
                <ColumnMeta>
                  <GhostPill style={{ width: row.labelWidth }} />
                  <GhostPill style={{ width: row.countWidth }} />
                </ColumnMeta>
              </BarColumn>
            ))
          : chartRows.map((row) => (
              <Column
                key={row.spaceId}
                data-bar-column
                onPress={() => {
                  recordInteraction("workflows.panel_drilldown_open", {
                    targetType: "space",
                    targetId: row.spaceId,
                    value: "executions",
                  });
                  navigate(routes.monitoring.workflows, {
                    panel: "executions",
                    space: row.spaceId,
                    search: "",
                  });
                }}
              >
                <BarRow>
                  <Bar
                    data-bar
                    style={{
                      background: colorMap.get(row.spaceId)!,
                      minHeight: row.executionCount > 0 ? 6 : 0,
                      height: `${executionCountBarFraction(row.executionCount, maxCount) * 100}%`,
                    }}
                  />
                </BarRow>
                <ColumnMeta>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {row.label}
                  </Text>
                  <Text
                    variant="text-small-md"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {formatExecutionCount(row.executionCount)}
                  </Text>
                </ColumnMeta>
              </Column>
            ))}
      </FadeScrollColumns>

      <Modal open={open} onClose={close} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle
            icon="play"
            title="Executions"
            space={selectedRow?.space ?? undefined}
          />
          <ModalHeaderRight>
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title="Busiest workflows"
            description="Ranked by executions on the live branch over the last 30 days."
            action={
              <SearchField
                placeholder="Search workflows…"
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            <ListTable
              headers={{
                name: "Workflow",
                executions: "Executions",
                lastRun: "Last run",
              }}
              columns={{ name: "flex", executions: "md", lastRun: "md" }}
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  {search
                    ? "No workflows match your search."
                    : "No live-branch runs in this space in the last 30 days."}
                </Text>
              }
              items={filteredWorkflowRows.map((row) => ({
                key: row.workflowId,
                link: makeUrl(routes.space.workflow, {
                  spaceId: row.spaceId,
                  workflowId: row.workflowId,
                }),
                name: (hover: boolean) => (
                  <NameCell>
                    <WorkflowBadge
                      workflow={{
                        id: row.workflowId,
                        badgeSeed: row.badgeSeed,
                        pausedAt: row.pausedAt,
                        branches: [{ name: "main" }],
                      }}
                      hover={hover}
                    />
                    <NameBody>
                      <Text variant="text-regular-md" ellipsis>
                        {row.name}
                      </Text>
                    </NameBody>
                  </NameCell>
                ),
                executions: (
                  <Text variant="text-small">
                    {formatExecutionCount(row.executionCount)}
                  </Text>
                ),
                lastRun: (
                  <Text variant="text-small">
                    {row.lastRunAt != null
                      ? formatRelativeTime(row.lastRunAt)
                      : "—"}
                  </Text>
                ),
              }))}
            />
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {selectedRow && (
              <>
                {formatExecutionCount(selectedRow.executionCount)} executions
                <MetaSep />
                {selectedRow.workflowCount}{" "}
                {selectedRow.workflowCount === 1 ? "workflow" : "workflows"}
              </>
            )}
          </Text>
          <Button size="l" variant="primary" onClick={close}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </Panel>
  );
}
